import { Suspense, lazy, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { DenLogoLockup } from '@/components/brand/den-logo';

/* -------------------------------------------------------------------------- */
/*  Sign-in chrome — applied-labs-inspired.                                    */
/*                                                                            */
/*  BrandAside: the left column — a full-bleed, deep ember gradient graphic    */
/*  panel with a slowly rotating hero point-globe (ported from the marketing   */
/*  site), carrying the Den logo, headline, and trust points in white. The     */
/*  right column (white form) lives in index.tsx.                             */
/* -------------------------------------------------------------------------- */

// Lazy-loaded so the three.js bundle never blocks the sign-in form.
const HeroSphere = lazy(() => import('./hero-sphere'));

// Where the brand logo links out to.
const DEN_MARKETING_URL = 'https://onchainden.com';

// Deep ember gradient — near-black base warming into a muted amber. Kept dark
// so the hero globe's warm points read clearly on top.
const WARM_GRADIENT =
  'radial-gradient(circle at 24% 78%, rgba(188, 90, 44, 0.32) 0%, transparent 44%),' +
  'radial-gradient(circle at 82% 16%, rgba(210, 150, 66, 0.20) 0%, transparent 46%),' +
  'linear-gradient(132deg, rgb(8, 3, 2) 0%, rgb(38, 13, 7) 44%, rgb(96, 44, 20) 78%, rgb(140, 86, 36) 100%)';

// Matches a CSS media query reactively — used to keep the three.js globe (and
// its WebGL context) off screens where the brand panel is hidden.
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    setMatches(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// Dense translucent-white dot matrix.
const DOTS = 'radial-gradient(rgba(255,255,255,0.07) 0.9px, transparent 1px)';

// Fractal-noise grain, inlined as a data URI.
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/** Texture layers over the gradient — dot matrix + grain + top sheen. */
function PanelTextures() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ backgroundImage: DOTS, backgroundSize: '5px 5px' }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-soft-light"
        style={{ backgroundImage: GRAIN }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/20 to-transparent"
      />
    </>
  );
}

/**
 * The panel's static backdrop — ember gradient + textures — used as the base
 * layer below the globe. The reveal cover reuses the same gradient + textures.
 */
function PanelChrome() {
  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0 saturate-[0.5]"
        style={{ background: WARM_GRADIENT }}
      />
      <PanelTextures />
    </>
  );
}

/**
 * The left column: a full-bleed, textured warm-gradient graphic panel holding
 * the Den logo (rendered white), a headline, and trust points.
 */
export function BrandAside({ className }: { className?: string }) {
  // Only mount the WebGL globe where the panel is actually visible (lg+).
  const showSphere = useMediaQuery('(min-width: 1024px)');
  // Fade the cover (below) away only once the globe has drawn real frames.
  const [sphereReady, setSphereReady] = useState(false);

  // Case: crossing below the lg breakpoint unmounts both the globe and its
  // cover, but sphereReady would otherwise persist as `true` from an earlier
  // ready state. Reset it so a later remount starts hidden behind an opaque
  // cover again — without this the cover renders at opacity-0 over the freshly
  // mounted, not-yet-drawn WebGL canvas, reintroducing the Arc/Chromium white
  // first-composite flash this cover exists to hide.
  useEffect(() => {
    if (!showSphere) setSphereReady(false);
  }, [showSphere]);

  return (
    <aside
      className={cn(
        // `isolate` (isolation: isolate) makes this a stacking context so the
        // reveal-cover (z-5) and content (z-10) order deterministically above
        // the globe's WebGL canvas. Without a contained stacking context, Arc
        // can composite the canvas layer above the cover, letting its pre-paint
        // flash leak through.
        'relative isolate flex-col items-start justify-between overflow-hidden p-12 xl:p-16',
        className
      )}
    >
      {/* Panel backdrop (gradient + dot matrix + grain + sheen), below the globe. */}
      <PanelChrome />

      {/* Rotating hero point-globe (desktop only), anchored to the bottom-right
          and allowed to bleed off the panel's right and bottom edges. Its inner
          (top-left) edge is softly dissolved by a radial mask. */}
      {showSphere && (
        <Suspense fallback={null}>
          <div
            aria-hidden
            className="pointer-events-none absolute aspect-square"
            style={{
              height: '120%',
              right: '-50%',
              bottom: '-50%',
              maskImage:
                'radial-gradient(circle closest-side, black 62%, transparent 94%)',
              WebkitMaskImage:
                'radial-gradient(circle closest-side, black 62%, transparent 94%)',
            }}
          >
            <HeroSphere
              className="opacity-[0.63]"
              onReady={() => setSphereReady(true)}
            />
          </div>
        </Suspense>
      )}

      {/* Reveal cover: a full copy of the backdrop laid over the globe, dissolved
          only once the globe has drawn real frames. A freshly-composited
          transparent WebGL canvas shows a one-tick opaque (white) layer in
          Chromium/Arc; this plain-CSS cover hides that window, then fades to
          reveal the globe.
          CRITICAL: the gradient is set as this element's OWN background (not via
          a child), so the z-[5] + translateZ compositor layer is itself opaque.
          That's what reliably composites above the WebGL canvas in Arc — a
          transparent cover whose children paint the gradient does NOT. The
          textures ride on top as children; base textures below re-appear once
          this fades. It stays below the z-10 content. */}
      {showSphere && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[5] saturate-[0.5]"
          style={{
            background: WARM_GRADIENT,
            transform: 'translateZ(0)',
            // Opacity + transition are set inline, NOT via Tailwind
            // (`transition-opacity duration-[2400ms]`), on purpose: Tailwind's
            // `transition-opacity` bakes in a 150ms duration, and whether it or
            // `duration-[2400ms]` wins depends on the generated CSS source
            // order — which shifts when unrelated classes are added elsewhere.
            // A silent drop to 150ms shortens this dissolve so much that the
            // globe canvas's white first-composite peeks through on reveal in
            // Chromium/Arc (the flash this cover exists to hide). The slow fade
            // is load-bearing, so it's pinned here where nothing can reorder it.
            opacity: sphereReady ? 0 : 1,
            transition: 'opacity 2400ms ease-out',
          }}
        >
          <PanelTextures />
        </div>
      )}

      {/* Den lockup: the fox mark keeps its brand colors; the wordmark text is
          filled with currentColor, so text-white renders just the text white.
          Links out to the marketing site with a subtle hover affordance. */}
      <a
        href={DEN_MARKETING_URL}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Den — visit onchainden.com"
        className="relative z-10 shrink-0 origin-left hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-4 focus-visible:ring-offset-transparent"
      >
        <DenLogoLockup className="h-7 w-auto text-white" />
      </a>

      <div className="relative z-10 max-w-[24rem]">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-white/70">
          Multi-layer security
        </p>
        <h2 className="mt-4 text-[2.4rem] font-normal leading-[1.1] tracking-tight text-white">
          Treasury moves that{' '}
          <span className="text-white/60">follow the rules.</span>
        </h2>
      </div>
      <div />
    </aside>
  );
}
